import { useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { SessionDetails } from '@/src/dal/session-dal'
import { VoiceAssessmentBetaBadge } from '@/src/features/voice-assessment/components/VoiceAssessmentBetaBadge'
import { colors } from '@/src/theme'
import { body3, body4, headline3, label1 } from '@/src/theme/typography'
import { useTranslation } from 'react-i18next'

import type { VoiceAssessmentRangeTimeframe } from '../../utils/voice-assessment-range-series'
import { noteRangeLabel } from '../../utils/session-analysis-metrics'
import { buildVoiceAssessmentRangeSeries } from '../../utils/voice-assessment-range-series'
import { VoiceAssessmentStatisticSegmentedControl } from './VoiceAssessmentStatisticSegmentedControl'
import { VoiceAssessmentStatisticsRangeBandChart } from './VoiceAssessmentStatisticsRangeBandChart'

interface VoiceAssessmentRangeCardProps {
  sessions?: Pick<
    SessionDetails,
    | 'score'
    | 'created'
    | 'comfortableMax'
    | 'comfortableMin'
    | 'pitchMax'
    | 'pitchMin'
  >[]
}

const EMPTY_RANGE_SESSIONS: NonNullable<
  VoiceAssessmentRangeCardProps['sessions']
> = []

export function VoiceAssessmentRangeCard({
  sessions = EMPTY_RANGE_SESSIONS,
}: VoiceAssessmentRangeCardProps) {
  const { t, i18n } = useTranslation(['statistics'])
  const [rangeTimeframe, setRangeTimeframe] =
    useState<VoiceAssessmentRangeTimeframe>('weekly')

  const latestCompletedSession = useMemo(
    () =>
      sessions.reduce<(typeof sessions)[number] | undefined>(
        (latest, session) =>
          latest == null || session.created.getTime() > latest.created.getTime()
            ? session
            : latest,
        undefined,
      ),
    [sessions],
  )

  const rangeSeries = useMemo(
    () =>
      buildVoiceAssessmentRangeSeries({
        sessions,
        timeframe: rangeTimeframe,
        locale: i18n.language,
      }),
    [i18n.language, rangeTimeframe, sessions],
  )

  const rangeOptions = useMemo(
    () =>
      [
        {
          key: 'weekly' as const,
          label: t('statistics:voice_assessment_segment_weekly'),
        },
        {
          key: 'monthly' as const,
          label: t('statistics:voice_assessment_segment_monthly'),
        },
        {
          key: 'sixMonths' as const,
          label: t('statistics:voice_assessment_segment_six_months'),
        },
      ] as const,
    [t],
  )
  const isPlaceholder = latestCompletedSession == null

  return (
    <View className="gap-2 rounded-lg bg-background-secondary px-4 py-3">
      <View className="flex-row items-center gap-2">
        <Text style={[label1, { color: colors.textBody }]}>
          {t('statistics:voice_assessment_range_section_title')}
        </Text>
        <VoiceAssessmentBetaBadge testID="range-beta-badge" />
      </View>
      <Text style={[body4, { color: colors.textBody }]}>
        {t('statistics:voice_assessment_range_current_heading')}
      </Text>
      <View className="flex-row gap-3">
        <View className="flex-1 rounded-lg bg-background-primary p-3">
          <Text style={[body3, { color: colors.textBody, marginBottom: 4 }]}>
            {t('statistics:voice_assessment_comfortable_range_label')}
          </Text>
          <Text style={[headline3, { color: colors.textHeader }]}>
            {isPlaceholder
              ? t('statistics:voice_assessment_placeholder_dash')
              : noteRangeLabel(
                  latestCompletedSession.comfortableMin,
                  latestCompletedSession.comfortableMax,
                )}
          </Text>
        </View>
        <View className="flex-1 rounded-lg bg-background-primary p-3">
          <Text style={[body3, { color: colors.textBody, marginBottom: 4 }]}>
            {t('statistics:voice_assessment_pitch_range_label')}
          </Text>
          <Text style={[headline3, { color: colors.textHeader }]}>
            {isPlaceholder
              ? t('statistics:voice_assessment_placeholder_dash')
              : noteRangeLabel(
                  latestCompletedSession.pitchMin,
                  latestCompletedSession.pitchMax,
                )}
          </Text>
        </View>
      </View>
      <Text style={[body4, { color: colors.textBody }]}>
        {t('statistics:voice_assessment_range_historic_heading')}
      </Text>
      <VoiceAssessmentStatisticSegmentedControl
        value={rangeTimeframe}
        onChange={setRangeTimeframe}
        options={[...rangeOptions]}
      />
      <VoiceAssessmentStatisticsRangeBandChart
        points={rangeSeries.points}
        xLabels={rangeSeries.xLabels}
        visibleLabelCount={rangeSeries.visibleLabelCount}
        columnWidthMultiplier={rangeSeries.columnWidthMultiplier}
        totalDays={rangeSeries.totalDays}
        locale={i18n.language}
      />
    </View>
  )
}
